import { Injectable } from '@angular/core';
import { translate } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { NotificationEvent } from '../../interface/notifications-event.interface';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import {
    AlertError,
    AlertSuccess,
    CheckAppPollEvents,
    CheckReconPollEvents,
    ClearEventPolling,
    ClearNotifications,
    EmailResent,
    GetNotifications,
    GetNotificationsViaPagination,
    GetReconEvents,
    PollForEvents,
    PollingTerminated,
    SetEmailNotifications,
    SetNotifications,
    SetReconEvents,
} from '../actions';
import * as actions from '../actions/notifications-event.actions';
import {
    getCurrentOrganizationId$,
    getEmailNotifications$,
    getEmailNotificationsViaPagination$,
    getEventDefinitions$,
    getEventSubscriptions$,
    getEventSubscriptionsViaPagination$,
    getEventTypes$,
    getNotificationsMap$,
    getNotificationsPagingState$,
    getNotificationsPagingStateMap$,
    getReconEventsMap$,
    getSubscribedAppsMap$,
} from '../selectors';

@Injectable()
export class NotificationEffects {
    private pollingResource: { [property: string]: any } = {};
    private pollingCounter: { [property: string]: number } = {};
    private pollingResourceType: { [property: string]: string } = {};
    private requestEvents = ['REQUEST_CREATED', 'REQUEST_CANCELLED', 'REQUEST_COMPLETED', 'INSTANCES_CREATED', 'REQUEST_TERMINATED'];
    private instanceEvents = ['TASK_STARTED', 'TASK_FAILED', 'TASK_COMPLETED'];
    private reportEndEvents = [
        'OUTBOUND_TRANSFORMATION_STARTED',
        'OUTBOUND_TRANSFORMATION_SKIPPED',
        'OUTBOUND_TRANSFORMATION_COMPLETED',
        'OUTBOUND_TRANSFORMATION_TERMINATED',
    ];
    private auditReportEndEvents = [
        'OUTBOUND_TRANSFORMATION_STARTED',
        'OUTBOUND_TRANSFORMATION_SKIPPED',
        'OUTBOUND_TRANSFORMATION_COMPLETED',
        'OUTBOUND_TRANSFORMATION_TERMINATED',
    ];
    private bulkReportsEndEvents = ['BULK_REPORTS_DOWNLOAD_COMPLETED', 'BULK_REPORTS_DOWNLOAD_TERMINATED'];
    private reconEndEvents = ['RECON_RESULT_AGGREGATION_COMPLETED', 'INBOUND_TRANSFORMATION_FAILED', 'RECON_FAILED', 'FAILED'];

    private loadingNotifications: { [resourceId: string]: boolean } = {};

    constructor(private store$: Store, private actions$: Actions, private _api: ApiService) {}

    pollForEvents = createEffect(() =>
        this.actions$.pipe(
            ofType(PollForEvents),
            mergeMap(action => {
                const pollingResourceType = this.pollingResourceType[action.resourceId];
                if (this.pollingResource[action.resourceId] && action.resourceType === pollingResourceType) {
                    return [];
                } else if (this.pollingResource[action.resourceId] && action.resourceType !== pollingResourceType) {
                    clearInterval(this.pollingResource[action.resourceId]);
                }
                // action.delay && this.store$.dispatch(
                //     ClearNotifications({
                //         resourceId: action.resourceId,
                //     })
                // );
                !action.delay && this.dispatchEvents(action);
                this.pollingCounter[action.resourceId] = 0;
                this.pollingResourceType[action.resourceId] = action.resourceType;
                this.pollingResource[action.resourceId] = setInterval(() => {
                    this.dispatchEvents(action);
                    this.pollingCounter[action.resourceId] += 1;
                }, 10000);
                return [];
            })
        )
    );

    private dispatchEvents = (action: {
        resourceId: string;
        appType: 'APP' | 'RECON' | 'MASTER';
        resourceType: 'REQUEST' | 'INSTANCE' | 'REPORTS' | 'BULK_REPORTS' | 'AUDIT_REPORTS';
        serviceId: string;
        timeStamp: number;
        noAutoClear?: boolean;
        noAlerts?: boolean;
    }) => {
        this.store$.dispatch(
            ClearNotifications({
                resourceId: action.resourceId,
            })
        );
        switch (action.appType) {
            case 'APP':
            case 'MASTER':
                this.store$.dispatch(
                    GetNotifications({
                        eventType: undefined,
                        fetchSize: 100,
                        resourceId: action.resourceId,
                        noAlerts: action.noAlerts,
                        noPagingState: true,
                        pollingResourceType: action.resourceType,
                        timeStamp: action.timeStamp,
                        noAutoClear: action.noAutoClear,
                    })
                );
                break;
            case 'RECON':
                this.store$.dispatch(
                    GetReconEvents({
                        resourceId: action.resourceId,
                        serviceId: action.serviceId,
                        noAlerts: true,
                        pollingResourceType: action.resourceType as 'REQUEST',
                    })
                );
                break;
        }
    };

    getNotifications = createEffect(() =>
        this.actions$.pipe(
            ofType(GetNotifications),
            delay(10),
            withLatestFrom(this.store$.select(getNotificationsPagingStateMap$)),
            mergeMap(([action, pagination]) => {
                if (typeof action.noPagingState === 'string' && action.noPagingState === 'end' && !action.noPagingState) {
                    return [];
                }
                return from(
                    this._api.notifications.getNotifications({
                        eventType: action.eventType,
                        fetchSize: action.fetchSize,
                        noAlerts: action.noAlerts,
                        pagingState: action.noPagingState ? undefined : pagination?.[action.resourceId]?.pagingState,
                        resourceId: action.resourceId,
                    })
                ).pipe(
                    map(res => {
                        action.pollingResourceType &&
                            this.store$.dispatch(
                                CheckAppPollEvents({
                                    resourceId: action.resourceId,
                                    resourceType: action.pollingResourceType,
                                    timeStamp: action.timeStamp,
                                    noAutoClear: action.noAutoClear,
                                })
                            );
                        return SetNotifications({
                            notifications: res?.result,
                            resourceId: action.resourceId,
                            pagingState: res?.pagingState || 'end',
                        });
                    }),
                    catchError(e => of(AlertError({ message: e })))
                );
            })
        )
    );

    getNotificationsViaPagination = createEffect(() =>
        this.actions$.pipe(
            ofType(GetNotificationsViaPagination, actions.SetPagination),
            delay(10),
            withLatestFrom(this.store$.select(getNotificationsPagingState$), this.store$.select(getNotificationsMap$)),
            mergeMap(([action, pagination, eventsMap]) => {
                const resourceId = action.resourceId;
                if (this.loadingNotifications[resourceId]) {
                    return [];
                }
                const logIds = eventsMap?.[resourceId]?.ids as string[];
                const paginationMap = pagination?.[resourceId];
                const expectedLength = paginationMap?.size * ((paginationMap?.index ?? -1) + 1);
                if (logIds && logIds.length > 0 && logIds.length >= expectedLength) {
                    return [];
                }
                this.loadingNotifications[resourceId] = true;
                return from(
                    this._api.notifications.getNotifications({
                        eventType: action.eventType,
                        fetchSize: paginationMap?.size,
                        resourceId,
                        noAlerts: action?.['noAlerts'],
                        pagingState: paginationMap?.noPagingState ? undefined : paginationMap?.pagingState,
                    })
                ).pipe(
                    map(res => {
                        this.loadingNotifications[resourceId] = false;
                        return SetNotifications({
                            notifications: res?.result,
                            resourceId,
                            pagingState: res?.pagingState,
                            eventType: action.eventType,
                        });
                    }),
                    catchError(e => {
                        this.loadingNotifications[resourceId] = false;
                        return of(AlertError({ message: e }));
                    })
                );
            })
        )
    );

    getReconEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetReconEvents),
            delay(10),
            withLatestFrom(this.store$.select(getSubscribedAppsMap$)),
            mergeMap(([action, appsMap]) => {
                const app = appsMap?.[action.serviceId];
                if (!app) {
                    return [];
                }
                return from(
                    this._api.requests.getReconciliationLogs({
                        noAlerts: action.noAlerts,
                        requestId: action.resourceId,
                        restApiName: app.restApiName,
                    })
                ).pipe(
                    map(res => {
                        res?.forEach(item => {
                            item.eventTime = CommonUtilsService.transformDateToLocale(
                                item.eventTime,
                                'YYYY-MM-DDTHH:MM:SSZ',
                                'ddMMMyyyy HH:MM:SS AM',
                                false
                            );
                        });
                        switch (action.pollingResourceType) {
                            case 'REQUEST':
                                this.store$.dispatch(
                                    CheckReconPollEvents({
                                        resourceId: action.resourceId,
                                        resourceType: action.pollingResourceType,
                                    })
                                );
                                break;
                        }
                        return SetReconEvents({
                            events: res,
                            resourceId: action.resourceId,
                            pagingState: 'end',
                        });
                    }),
                    catchError(e => of(AlertError({ message: e })))
                );
            })
        )
    );

    checkAppPollEvents = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckAppPollEvents),
            delay(100),
            filter(data => this.pollingResource[data.resourceId] !== undefined),
            withLatestFrom(this.store$.select(getNotificationsMap$)),
            mergeMap(([action, eventsMap]) => {
                const logIds = eventsMap?.[action.resourceId]?.ids as string[];
                const logsMap = eventsMap?.[action.resourceId]?.entities;
                if (!logIds || !logsMap || !this.pollingResource[action.resourceId]) {
                    return [];
                }
                let events: string[] = [];
                switch (action.resourceType) {
                    case 'REQUEST':
                        events = this.requestEvents;
                        break;
                    case 'INSTANCE':
                        events = this.instanceEvents;
                        break;
                    case 'REPORTS':
                        events = this.reportEndEvents;
                        break;
                    case 'AUDIT_REPORTS':
                        events = this.auditReportEndEvents;
                        break;
                    case 'BULK_REPORTS':
                        events = this.bulkReportsEndEvents;
                        break;
                }
                const eventId = logIds
                    ?.filter(id => !action.timeStamp || new Date(logsMap?.[id].createdOn)?.getTime() > action.timeStamp)
                    ?.find(id => events.includes(logsMap?.[id]?.eventType));
                const count = this.pollingCounter[action.resourceId];
                if (eventId !== undefined) {
                    if (action.resourceType === 'INSTANCE' && logsMap[eventId].eventType === 'TASK_STARTED') {
                        // Do Nothing
                    } else if (
                        action.resourceType === 'REQUEST' &&
                        ((logsMap[eventId].eventType === 'REQUEST_CREATED' && logsMap[eventId]?.payload?.sourceType === 'PSG') ||
                            logsMap[eventId].eventType !== 'REQUEST_CREATED')
                    ) {
                        return of(ClearEventPolling({ resourceId: action.resourceId }));
                    } else if (action.resourceType === 'REPORTS' || action.resourceType === 'AUDIT_REPORTS') {
                        if (logsMap[eventId].eventType === 'OUTBOUND_TRANSFORMATION_STARTED') {
                            // Do Nothing
                        } else if (this.reportEndEvents.includes(logsMap[eventId].eventType)) {
                            return of(ClearEventPolling({ resourceId: action.resourceId }));
                        }
                    } else if (action.resourceType === 'BULK_REPORTS') {
                        if (logsMap[eventId].eventType === 'BULK_REPORTS_DOWNLOAD_STARTED') {
                            // Do Nothing
                        } else if (this.bulkReportsEndEvents.includes(logsMap[eventId].eventType)) {
                            return of(ClearEventPolling({ resourceId: action.resourceId }));
                        }
                    } else if (action.resourceType !== 'REQUEST') {
                        if (
                            !logsMap[eventId]?.payload?.doneForNow &&
                            !logsMap[eventId].eventType.includes('_COMPLETED') &&
                            !logsMap[eventId].eventType.includes('_FAILED')
                        ) {
                            return [];
                        }
                        return of(ClearEventPolling({ resourceId: action.resourceId }));
                    }
                }
                if (action.resourceType === 'REPORTS' && count >= 4 && !action.noAutoClear) {
                    this.store$.dispatch(
                        PollingTerminated({
                            resourceId: action.resourceId,
                            resourceType: action.resourceType,
                            timeStamp: action.timeStamp,
                        })
                    );
                    return of(ClearEventPolling({ resourceId: action.resourceId }));
                }
                return [];
            })
        )
    );

    checkReconPollEvents = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckReconPollEvents),
            delay(100),
            filter(data => this.pollingResource[data.resourceId] !== undefined),
            withLatestFrom(this.store$.select(getReconEventsMap$)),
            mergeMap(([action, eventsMap]) => {
                const logIds = eventsMap?.[action.resourceId]?.ids as string[];
                const logsMap = eventsMap?.[action.resourceId]?.entities;
                if (!logIds || !logsMap || !this.pollingResource[action.resourceId]) {
                    return [];
                }
                let events: string[] = [];
                switch (action.resourceType) {
                    case 'REQUEST':
                        events = this.reconEndEvents;
                        break;
                }
                const event = logIds?.find(id => events.includes(logsMap?.[id]?.status));
                if (event !== undefined) {
                    this.pollingCounter[action.resourceId] = undefined;
                    return of(ClearEventPolling({ resourceId: action.resourceId }));
                }
                return [];
            })
        )
    );

    clearEventPolling = createEffect(() =>
        this.actions$.pipe(
            ofType(ClearEventPolling),
            filter(data => this.pollingResource?.[data.resourceId] !== undefined),
            mergeMap(action => {
                clearInterval(this.pollingResource[action.resourceId]);
                this.pollingResource[action.resourceId] = undefined;
                this.pollingCounter[action.resourceId] = undefined;
                return [];
            })
        )
    );

    getInstanceProcessLogs = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetProcessLogs, actions.ClearProcessLogs),
            delay(10),
            mergeMap(action => {
                if (action.type === actions.types.CLEAR_PROCESS_LOGS) {
                    return [];
                }
                return this._api.common
                    .getProcessLogs({
                        resourceId: action.resourceId,
                        fetchSize: action.fetchSize,
                        pagingState: action.pagingState,
                    })
                    .pipe(
                        map((res: { result: NotificationEvent[]; fetchSize: number; pagingState: string }) => {
                            return actions.SetProcessLogs({
                                processLogs: res?.result,
                                resourceId: action.resourceId,
                                pagingState: res?.pagingState || 'end',
                            });
                        }),
                        catchError(e => of(AlertError({ message: e })))
                    );
            })
        );
    });

    getEventDefinitions = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetEventDefinitions),
            withLatestFrom(this.store$.select(getEventDefinitions$)),
            mergeMap(([_action, eventDefinitions]) => {
                if (eventDefinitions?.length > 0) {
                    return [];
                }
                return from(this._api.events.getEventDefinitions(_action.forSubscription)).pipe(
                    map((res: any) => {
                        return actions.SetEventDefinitions({
                            eventDefinitions: res?.response?.definitions,
                        });
                    }),
                    catchError(error => of(AlertError({ message: error?.msg })))
                );
            })
        )
    );

    getEventTypes = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetEventTypes),
            withLatestFrom(this.store$.select(getEventTypes$)),
            mergeMap(([_action, eventTypes]) => {
                if (eventTypes?.['ASSET']?.length > 0) {
                    return [];
                }
                return from(this._api.events.getEventTypes(_action?.forSubscription)).pipe(
                    map((res: any) => {
                        return actions.SetEventTypes({
                            eventTypes: res,
                        });
                    }),
                    catchError(error => of(AlertError({ message: error?.msg })))
                );
            })
        )
    );

    getEventSubscriptions = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetSubscriptionsViaPagination),
            withLatestFrom(this.store$.select(getEventSubscriptionsViaPagination$), this.store$.select(getEventSubscriptions$)),
            delay(10),
            mergeMap(([action, eventSubscriptions, allSubscriptions]) => {
                const workflowEventTypes = ['TASK_STARTED', 'TASK_COMPLETED'];
                const filteredEventTypes = action.eventType.filter(eventType => {
                    if (workflowEventTypes.includes(eventType)) {
                        const hasWorkflowEvent = eventSubscriptions.some(subscription =>
                            workflowEventTypes.includes(subscription.eventType.toUpperCase())
                        );
                        if (hasWorkflowEvent) {
                            return false;
                        }
                    }
                    return true;
                });

                if (filteredEventTypes.length > 0 && eventSubscriptions?.length > 0) {
                    for (let i = 0; i < filteredEventTypes.length; i++) {
                        const eventType = filteredEventTypes[i];
                        if (allSubscriptions.some(subscription => subscription.eventType === eventType.toLowerCase())) {
                            filteredEventTypes.splice(i, 1);
                            i--;
                        }
                    }
                }
                if (filteredEventTypes.length === 0) {
                    return [];
                }
                if (action.index > 0 && !action.pagingState) {
                    return [];
                }
                return from(
                    this._api.events.getSubscribedEvents({
                        orgId: action.orgId,
                        fetchSize: action.fetchSize,
                        pagingState: action.pagingState,
                        eventType: filteredEventTypes,
                        groupId: action.groupId,
                    })
                ).pipe(
                    map((res: { result: any[]; fetchSize: number; pagingState: string }) => {
                        return actions.SetEventSubscriptions({
                            fetchSize: res?.fetchSize,
                            pagingState: res?.pagingState?.length > 0 ? res?.pagingState : undefined,
                            index: action.index,
                            eventSubscriptions: res?.result,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e.msg })))
                );
            })
        )
    );

    deleteSubscription$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.DeleteEventSubscription),
            mergeMap(action => {
                return this._api.events.deleteEventSubscription(action.data).pipe(
                    map((res: { msg: string }) => {
                        this.store$.dispatch(AlertSuccess({ message: res?.msg || translate('Event subscription deleted successfully') }));
                        return actions.DeleteEventSubscriptionSuccess({ subscriptionId: action?.data?.subscriptionId });
                    }),
                    catchError(e => {
                        return of(AlertError({ message: e?.msg || translate('Failed to delete event subscription') }));
                    })
                );
            })
        );
    });

    updateSubscription$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.UpdateEventSubscription),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, organizationId]) => {
                return this._api.events.editEventSubscriptions(action.data).pipe(
                    map((res: { msg: string }) => {
                        this.store$.dispatch(AlertSuccess({ message: res?.msg || translate('Event subscription updated successfully') }));
                        this.store$.dispatch(actions.ClearEventSubscriptions());
                        const data = {
                            orgId: organizationId,
                            fetchSize: 10,
                            pagingState: '',
                            eventType: [''],
                            index: 0,
                            groupId: action.data.groupId,
                        };
                        if (action.data.eventType === 'OUTBOUND_TRANSFORMATION_COMPLETED') {
                            data.eventType = ['OUTBOUND_TRANSFORMATION_COMPLETED'];
                            return actions.GetSubscriptionsViaPagination(data);
                        } else {
                            data.eventType = ['TASK_STARTED', 'TASK_COMPLETED'];
                            return actions.GetSubscriptionsViaPagination(data);
                        }
                    }),
                    catchError(e => {
                        return of(AlertError({ message: e?.msg || translate('Failed to update event subscription') }));
                    })
                );
            })
        );
    });

    createSubscription$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.CreateEventSubscription),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, organizationId]) => {
                return this._api.events.createEventSubscriptions(action.data).pipe(
                    map((res: { msg: string }) => {
                        this.store$.dispatch(AlertSuccess({ message: res?.msg || translate('Event subscription created successfully') }));
                        this.store$.dispatch(actions.ClearEventSubscriptions());
                        const data = {
                            orgId: organizationId,
                            fetchSize: 10,
                            pagingState: '',
                            eventType: [''],
                            index: 0,
                            groupId: action.data.groupId,
                        };
                        if (action.data.eventType === 'OUTBOUND_TRANSFORMATION_COMPLETED') {
                            data.eventType = ['OUTBOUND_TRANSFORMATION_COMPLETED'];
                            return actions.GetSubscriptionsViaPagination(data);
                        } else {
                            data.eventType = ['TASK_STARTED', 'TASK_COMPLETED'];
                            return actions.GetSubscriptionsViaPagination(data);
                        }
                    }),
                    catchError(e => {
                        return of(AlertError({ message: e?.msg || translate('Failed to create event subscription') }));
                    })
                );
            })
        );
    });

    getEmailNotifications = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.SetEmailPagination),
            withLatestFrom(this.store$.select(getEmailNotificationsViaPagination$)),
            delay(10),
            mergeMap(([action, emailNotifications]) => {
                if (emailNotifications?.length > 0) {
                    return [];
                }
                return from(
                    this._api.events.getEmailNotifications({
                        groupId: action.groupId,
                        eventType: action.eventType,
                        subscriptionId: action.subscriptionId,
                        size: action.size,
                        pageState: action.pageState,
                        fromDate: action.fromDate,
                        toDate: action.toDate,
                        status: action.status,
                        mailId: action.mailId,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map((res: { result: any[]; fetchSize: number; pagingState: string }) => {
                        return SetEmailNotifications({
                            fetchSize: res?.fetchSize,
                            emailNotifications: res?.result,
                            pagingState: res?.pagingState,
                            index: action.index,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e.msg })))
                );
            })
        )
    );

    getEmailReport = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetEmailReport),
            mergeMap(action => {
                return this._api.events
                    .getEmailReport({
                        groupId: action.groupId,
                        eventType: action.eventType,
                        subscriptionId: action.subscriptionId,
                        fromDate: action.fromDate,
                        toDate: action.toDate,
                        status: action.status,
                        mailId: action.mailId,
                        noAlerts: action.noAlerts,
                    })
                    .pipe(
                        map((res: any) => {
                            const url = window.URL.createObjectURL(res);
                            const a = document.createElement('a');
                            a.href = url;
                            a.click();
                            window.URL.revokeObjectURL(url);
                            return AlertSuccess({ message: 'Report downloaded successfully' });
                        }),
                        catchError(res => {
                            return of(AlertError({ message: res.msg || 'Failed to download report' }));
                        })
                    );
            })
        );
    });

    resendEmail = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.ResendEmail),
            delay(10),
            withLatestFrom(this.store$.select(getEmailNotifications$)),
            mergeMap(([action, events]) => {
                const event = events?.find(log => log.messageId === action.messageId && log.emailId === action.emailId);
                if (!event) {
                    return of(AlertError({ message: translate('Failed to resend email') }));
                }
                return this._api.events
                    .resendEmail({
                        orgId: event.orgId,
                        createdOn: event.createdOn,
                        messageId: event.messageId,
                        emailId: event.emailId,
                    })
                    .pipe(
                        mergeMap((res: { msg: string }) => {
                            return [
                                EmailResent({ messageId: event.messageId, emailId: event.emailId }),
                                AlertSuccess({ message: res.msg || '' }),
                            ];
                        }),
                        catchError(res => {
                            return of(AlertError({ message: res.msg || '' }));
                        })
                    );
            })
        );
    });
}
